<template>
    <div class="preparation-main-category-page">
        <transition name="loading-screen">
            <LoadingScreen v-if="loading" />
        </transition>
        <template v-if="errors">
            <ErrorsZone :errors="errors" />
        </template>
        <template v-if="loaded && !errors">
            <div class="row mb-4 align-items-center">
                <div class="col-md-6 col-12">
                    <h2>Catégorie : {{ category && category.name }}</h2>
                </div>
                <div class="col-md-6 col-12">
                    <OrdersPreparationProgressBars
                        :pickup-points-productions-status="progressCounters['pickup_points_productions']"
                        :pickup-points-subcustomers-status="progressCounters['pickup_points_subcustomers']"
                        :short-channels-status="progressCounters['short_channels']"
                        :customer-groups-status="progressCounters['customer_groups']"
                        :stores-status="progressCounters['stores']"
                    />
                </div>
            </div>

            <div class="row mt-4">
                <div class="col-md-12">
                    <h3>Produits</h3>
                </div>
            </div>

            <div class="row">
                <CategoryProductStatus
                    class="col-md-3"
                    v-for="product in products"
                    :key="product.sku"
                    :end-preparation-date="endPreparationDate"
                    :sku="product.sku"
                    :name="product.tiny_name"
                    :preparation-status="product.preparation_status"
                />
            </div>

            <div class="row mt-4">
                <div class="col-md-12">
                    <h3>Clients</h3>
                </div>
            </div>

            <div class="row">
                <div class="col-md-12">
                    <h4>Lieux de retrait</h4>
                </div>
            </div>

            <div v-if="_.values(pickupPointsAllOrders).length === 0" class="row">
                <div class="col-md-12">
                    <span class="nothing">Aucune commande client ou lieu de retrait</span>
                </div>
            </div>
            <div v-else v-for="pickupPoint in _.orderBy(_.values(pickupPointsAllOrders), ['name'])" class="row point-retrait mb-4">
                <div class="col-md-3 col-lg-2 py-2 pr-4 point-retrait-nom">
                    <PickupPointProductionOrderStatus
                        :key="pickupPoint.id"
                        :end-preparation-date="endPreparationDate"
                        :pickup-point="pickupPoint"
                        :order="pickupPoint.order"
                    />
                </div>

                <div class="col-md-9 col-lg-10 py-2">
                    <div class="row">
                        <div class="col-md-12">
                            <h6><OrderIcon type="pickup_point_subcustomer" :online="true"></OrderIcon> Click & collect :</h6>
                        </div>
                    </div>
                    <div class="row">
                        <template v-if="pickupPoint.subcustomers_orders.online.length === 0">
                            <div class="col-md-12">
                                <span class="nothing">Aucune commande click & collect</span>
                            </div>
                        </template>
                        <template v-else>
                            <PickupPointCustomerOrderStatus
                                v-for="order in pickupPoint.subcustomers_orders.online"
                                :key="order.id"
                                :id="order.id"
                                :reference="order.reference"
                                :name="order.name"
                                :preparation-status="order.preparation_status"
                                :paid-at="order.paid_at"
                                :online="true"
                                :status-label="order.status_label"
                                :has-comments="order.has_comments"
                                :has-priority="order.has_priority"
                            />
                        </template>
                    </div>

                    <div class="row mt-3">
                        <div class="col-md-12">
                            <h6><OrderIcon type="pickup_point_subcustomer" :online="false"></OrderIcon> Autres commandes:</h6>
                        </div>
                    </div>
                    <div class="row">
                        <template v-if="pickupPoint.subcustomers_orders.internal.length === 0">
                            <div class="col-md-12">
                                <span class="nothing">Aucune commande</span>
                            </div>
                        </template>
                        <template v-else>
                            <PickupPointCustomerOrderStatus
                                v-for="order in pickupPoint.subcustomers_orders.internal"
                                :key="order.id"
                                :id="order.id"
                                :reference="order.reference"
                                :name="order.name"
                                :preparation-status="order.preparation_status"
                                :paid-at="order.paid_at"
                                :online="false"
                                :status-label="order.status_label"
                                :has-comments="order.has_comments"
                                :has-priority="order.has_priority"
                            />
                        </template>
                    </div>
                </div>
            </div>

            <div class="row mt-4">
                <div class="col-md-12">
                    <h4>Circuits courts</h4>
                </div>
            </div>
            <div class="row">
                <div v-if="shortChannelsOrders.length === 0" class="col-md-12">
                    <span class="nothing">Aucune commande</span>
                </div>
                <StoreOrderStatus
                    v-else
                    v-for="order in _.orderBy(shortChannelsOrders, ['short_channel_name'])"
                    :key="order.id || 'short_channel' + order.short_channel_id"
                    :id="order.id"
                    :customer-id="order.short_channel_id"
                    :customer-type="order.type"
                    :end-preparation-date="endPreparationDate"
                    :reference="order.reference"
                    :name="order.short_channel_name"
                    :preparation-status="order.preparation_status"
                    :status-label="order.status_label"
                    :has-comments="order.has_comments"
                    :has-priority="order.has_priority"
                    :ready-to-prepare="order.ready_to_prepare"
                />
            </div>

            <div class="row mt-4">
                <div class="col-md-12">
                    <h4>Groupements de clients</h4>
                </div>
            </div>
            <div class="row">
                <div v-if="customerGroupsOrders.length === 0" class="col-md-12">
                    <span class="nothing">Aucune commande</span>
                </div>
                <CustomerGroupOrderStatus
                    v-else
                    v-for="order in _.orderBy(customerGroupsOrders, ['customer_group_name'])"
                    :key="order.id || 'customer_group' + order.customer_group_id"
                    :id="order.id"
                    :customer-id="order.customer_group_id"
                    :customer-type="order.type"
                    :end-preparation-date="endPreparationDate"
                    :reference="order.reference"
                    :name="order.customer_group_name"
                    :preparation-status="order.preparation_status"
                    :status-label="order.status_label"
                    :has-comments="order.has_comments"
                    :has-priority="order.has_priority"
                />
            </div>

            <div class="row mt-4">
                <div class="col-md-12">
                    <h4>Magasins</h4>
                </div>
            </div>
            <div class="row">
                <div v-if="storesOrders.length === 0" class="col-md-12">
                    <span class="nothing">Aucune commande</span>
                </div>
                <StoreOrderStatus
                    v-else
                    v-for="order in _.orderBy(storesOrders, ['store_name'])"
                    :key="order.id || 'store' + order.store_id"
                    :id="order.id"
                    :customer-id="order.store_id"
                    :customer-type="order.type"
                    :end-preparation-date="endPreparationDate"
                    :reference="order.reference"
                    :name="order.store_name"
                    :preparation-status="order.preparation_status"
                    :status-label="order.status_label"
                    :has-comments="order.has_comments"
                    :has-priority="order.has_priority"
                />
            </div>
        </template>
    </div>
</template>

<script>
    import CategoryProductStatus from "./MainCategoryPage/CategoryProductStatus";
    import ErrorsZone from "../ErrorsZone";
    import LoadingScreen from "../LoadingScreen";
    import OrderIcon from "./OrderIcon"
    import OrdersPreparationProgressBars from "./OrdersPreparationProgressBars";
    import PickupPointCustomerOrderStatus from "./MainPage/PickupPointCustomerOrderStatus"
    import PickupPointProductionOrderStatus from "./MainPage/PickupPointProductionOrderStatus"
    import StoreOrderStatus from "./MainPage/StoreOrderStatus"
    import {useGetLoader} from "../compositions/axios/loadingRequest";

    export default {
        name: 'PreparationMainCategoryPage',

        components: {
            CategoryProductStatus,
            ErrorsZone,
            LoadingScreen,
            OrderIcon,
            OrdersPreparationProgressBars,
            PickupPointCustomerOrderStatus,
            PickupPointProductionOrderStatus,
            StoreOrderStatus,
        },

        props: {
            categoryId: {
                type: String,
                required: true,
            },
            endPreparationDate: {
                type: String,
                required: true,
            },
        },

        setup() {
            const {
                loaded,
                loading,
                errors,
                load,
            } = useGetLoader();

            return {
                loaded,
                loading,
                errors,
                load,
            };
        },

        data() {
            return {
                category: null,
                products: [],
                storesOrders: [],
                shortChannelsOrders: [],
                customerGroupsOrders: [],
                pickupPointsAllOrders: [],
                progressCounters: {
                    stores: {
                        color: '',
                        counters: {
                            numerator: 0,
                            denominator: 0,
                            nb_toremove: 0,
                        },
                    },
                    short_channels: {
                        color: '',
                        counters: {
                            numerator: 0,
                            denominator: 0,
                            nb_toremove: 0,
                        },
                    },
                    customer_groups: {
                        color: '',
                        counters: {
                            numerator: 0,
                            denominator: 0,
                            nb_toremove: 0,
                        },
                    },
                    pickup_points_subcustomers: {
                        color: '',
                        counters: {
                            numerator: 0,
                            denominator: 0,
                            nb_toremove: 0,
                        },
                    },
                    pickup_points_productions: {
                        color: '',
                        counters: {
                            numerator: 0,
                            denominator: 0,
                            nb_toremove: 0,
                        },
                    },
                },
            }
        },

        async mounted() {
            this.loadStateFromServer();
        },

        methods: {
            async loadStateFromServer() {
                const data = await this.load(`/espace-employes/commandes/preparation/${this.endPreparationDate}/categorie-principale/${this.categoryId}/json`);
                if (data) {
                    this.category = data.category;
                    this.products = data.products;
                    this.storesOrders = data.storesOrders;
                    this.shortChannelsOrders = data.shortChannelsOrders;
                    this.customerGroupsOrders = data.customerGroupsOrders;
                    this.pickupPointsAllOrders = data.pickupPointsAllOrders;
                    this.progressCounters = data.progressCounters;
                }
            },
        },
    }
</script>

<style lang="scss" scoped>
    .preparation-main-category-page {
        .btn-point-retrait, .btn-order {
            border: 2px solid #252525;
            width: 100%;
            margin: 0 auto;
        }

        .point-retrait {
            @media (min-width: 768px) {
                .point-retrait-nom {
                    border-right: 3px solid #252525;
                }
            }

            .point-retrait-nom {
                .counter {
                    text-align: center;
                    font-weight: bold;
                    font-size: 1.0rem;
                }
            }
        }

        .nothing {
            font-style: italic;
            font-size: 1.3rem;
        }
    }
</style>
